import {
  ChangeEvent,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router'
import { ReactSVG } from 'react-svg'
import { useDebounceValue } from 'usehooks-ts'

import { useSearchHistoryStore } from '@features/search/model/useSearchStore'
import { useAddressStore } from '@features/shipping-methods'

import { useCategoriesModal } from '@widgets/modal-categories/hooks/useCategoriesModal'
import { ProductPreviewCard } from '@widgets/product-preview'
import { ProductPreviewCardProps } from '@widgets/product-preview/ui'

import { usePopularProductsQuery, useSearchProductsQuery } from '@shared/generated'
import Button from '@shared/ui/button'
import CardSkeleton from '@shared/ui/card-skeleton'

import EmptyPopover from './empty-popover/empty-popover'

import styles from './mobile-search-modal.module.scss'

interface MobileSearchModalProps {
  onClose: () => void
}

export default function MobileSearchModal ({ onClose }: MobileSearchModalProps) {
  const { t: tc } = useTranslation('common')
  const { t: ts } = useTranslation('search')
  const navigate = useNavigate()
  const inputRef = useRef<HTMLInputElement>(null)
  const [ inputValue, setInputValue ] = useState('')
  const [ debouncedValue ] = useDebounceValue(inputValue, 500)
  const { addQuery, history, clearHistory } = useSearchHistoryStore()
  const { shopId } = useAddressStore()
  const { openModal: openCategoriesModal } = useCategoriesModal()
  const { data: searchData, loading: searchLoading } = useSearchProductsQuery({
    skip: debouncedValue.trim() === '',
    variables: {
      shopId: shopId,
      searchQuery: {
        search: debouncedValue,
      },
      pager: {
        limit: 4,
        page: 1,
      },
    },
  })

  const { data: popularProducts } = usePopularProductsQuery({
    variables: {
      shopId: shopId,
      pager: {
        limit: 4,
        page: 1,
      },
    },
  })

  const HAS_RESULTS = useMemo(() => {
    return !!searchData?.searchProducts?.edges?.length
  }, [ searchData ])

  const IS_EMPTY = useMemo(() => {
    return !!(
      searchData?.searchProducts?.edges?.length === 0 && debouncedValue
    )
  }, [ searchData, debouncedValue ])

  useEffect(() => {
    // Focus input when modal opens
    inputRef.current?.focus()
  }, [])

  const handleSearch = (value?: string) => {
    const query = (value ?? inputValue).trim()

    if (!query) return

    if (query.length > 3) {
      addQuery(query)
    }

    navigate(`/search?query=${query}`)
    onClose()
  }

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.code === 'Enter' || e.code === 'NumpadEnter') {
      e.preventDefault()
      handleSearch()
    }
  }

  const handleGoToCatalog = () => {
    // navigate('/catalog')
    openCategoriesModal()
    onClose()
  }

  const handleHistoryClick = (value: string) => {
    setInputValue(value)
    inputRef.current?.focus()
  }

  // Frequently searched items (hardcoded for now)
  // const frequentlySearched = [
  //   'Молоко',
  //   'Макароны',
  //   'Масло сливочное',
  //   'Майонез',
  //   'Морковь',
  //   'Мука',
  //   'Мясо'
  // ]

  // Categories (hardcoded for now)
  // const categories = [
  //   { icon: '/icons/milk.svg', name: 'Молоко, яйца, сыр' },
  //   { icon: '/icons/chicken.svg', name: 'Мясо, птица' },
  //   { icon: '/icons/fish.svg', name: 'Рыба, морепродукты' }
  // ]

  return (
    <div className={styles.modal}>
      {/* Search Header */}
      <div className={styles.header}>
        <div className={styles.searchContainer}>
          <Button
            className={styles.backButton}
            onClick={onClose}
            shape='square'
            theme='none'
          >
            <ReactSVG src="/icons/arrow-left.svg" wrapper="span" />
          </Button>

          <div className={styles.inputWrapper}>
            <input
              ref={inputRef}
              className={styles.searchInput}
              placeholder={tc('search.button')}
              value={inputValue}
              onChange={(e: ChangeEvent<HTMLInputElement>) => {
                setInputValue(e.target.value)
              }}
              onKeyDown={handleKeyDown}
            />
            <button
              className={styles.searchButton}
              type="button"
              onClick={() => handleSearch()}
            >
              <ReactSVG src="/icons/search.svg" wrapper="span" />
            </button>
          </div>
        </div>
      </div>

      {/* Content Area */}
      <div className={styles.content}>

        {/* Search History */}
        {!inputValue && !searchData && !!history.length && (
          <div className={styles.history}>
            <div className={styles.historyHeader}>
              <h3 className={styles.sectionTitle}>{ts('searchHistory')}</h3>
              <button
                className={styles.clearHistory}
                type="button"
                onClick={clearHistory}
              >
                <ReactSVG src="/icons/cross.svg" wrapper="span" />
                <span className={styles.clearHistoryText}>{ts('clearHistory')}</span>
              </button>
            </div>
            <div className={styles.tags}>
              {history.map((h) => (
                <button
                  key={h}
                  className={styles.tag}
                  onClick={() => handleHistoryClick(h)}
                >
                  {h}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Frequently Searched */}
        {/* {!inputValue && !searchData && (
          <div className={styles.frequentlySearched}>
            <h3 className={styles.sectionTitle}>Часто ищут</h3>
            <div className={styles.tags}>
              {frequentlySearched.map((item, index) => (
                <button
                  key={index}
                  className={styles.tag}
                  onClick={() => handleSearch(item)}
                >
                  {item}
                </button>
              ))}
            </div>
          </div>
        )} */}

        {/* Categories */}
        {/* {!inputValue && !searchData && (
          <div className={styles.categories}>
            <h3 className={styles.sectionTitle}>Категории</h3>
            <div className={styles.categoryList}>
              {categories.map((category, index) => (
                <button
                  key={index}
                  className={styles.categoryItem}
                  onClick={() => navigate('/catalog')}
                >
                  <div className={styles.categoryIcon}>
                    <ReactSVG src={category.icon} wrapper="span" />
                  </div>
                  <span className={styles.categoryName}>{category.name}</span>
                </button>
              ))}
            </div>
          </div>
        )} */}

        {/* Products */}
        {!inputValue && !searchData && popularProducts?.popularProducts?.edges && (
          <div className={styles.products}>
            <h3 className={styles.sectionTitle}>{ts('popularProducts')}</h3>
            <div className={styles.productsGrid}>
              {popularProducts.popularProducts.edges.map((product) => (
                <div key={product?.id} onClick={onClose}>
                  <ProductPreviewCard
                    data={product as ProductPreviewCardProps['data']}
                  />
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Empty State */}
        {IS_EMPTY && (
          <EmptyPopover onGoToCatalog={handleGoToCatalog} />
        )}

        {/* Search Results */}
        {HAS_RESULTS && (
          <div className={styles.results}>
            <h3 className={styles.sectionTitle}>
              {ts('notEmpty.title', { query: inputValue })}
            </h3>
            <div className={styles.productsGrid}>
              {searchData?.searchProducts?.edges?.filter(p => !!p).map((product) => (
                <div key={product?.id} onClick={onClose}>
                  <ProductPreviewCard
                    data={product as ProductPreviewCardProps['data']}
                  />
                </div>
              ))}
            </div>
            <Button
              className={styles.viewAllButton}
              onClick={() => handleSearch()}
            >
              {ts('allResults')}
            </Button>
          </div>
        )}

        {/* Loading State */}
        {searchLoading && (
          <div className={styles.loading}>
            <div className={styles.productsGrid}>
              {Array.from({ length: 4 }).map((_, index) => (
                <CardSkeleton key={index} />
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  )
}
